import createRequest from '../../../shared/api/portalApiClient';
import type { ApiError } from '../../../shared/api/apiError';
import { env } from '../../../config/env';
import { storage } from '../../../shared/utils/storage';

export class BillingHttpError extends Error {
  status?: number;
  data?: unknown;

  constructor(message: string, status?: number, data?: unknown) {
    super(message);
    this.name = 'BillingHttpError';
    this.status = status;
    this.data = data;
  }
}

const apiBase = process.env.REACT_APP_API_BASE_URL || env.apiBase || '';
const request = createRequest(apiBase);

const safeRedirectToLogin = (): void => {
  try {
    storage.purgeLegacyWebAuthStorage();
  } catch (error) {
    // ignore
  }
  if (typeof window !== 'undefined') {
    window.location.assign('/login');
  }
};

const messageFromApiErrorData = (data: unknown): string => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const anyData = data as Record<string, unknown>;
  const msg = anyData.message;
  return typeof msg === 'string' ? msg : '';
};

const normalizeAndThrow = (error: unknown): never => {
  if (error && typeof error === 'object' && 'status' in error) {
    const apiErr = error as ApiError;
    const status = typeof apiErr.status === 'number' ? apiErr.status : undefined;
    const messageFromData = messageFromApiErrorData(apiErr.data);

    if (status === 401) {
      safeRedirectToLogin();
      throw new BillingHttpError(messageFromData || 'Session expired', 401, apiErr.data);
    }

    if (status && status >= 500) {
      throw new BillingHttpError('Server error. Please try again.', status, apiErr.data);
    }

    if (status && status >= 400) {
      throw new BillingHttpError(messageFromData || 'Request failed', status, apiErr.data);
    }
  }

  const message = error instanceof Error ? error.message : '';
  throw new BillingHttpError(message || 'Network error. Your card has not been charged.', 0);
};

const withTimeout = async <T>(fn: (signal: AbortSignal) => Promise<T>, timeoutMs = 15000): Promise<T> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fn(controller.signal);
  } catch (error) {
    return normalizeAndThrow(error);
  } finally {
    window.clearTimeout(timeout);
  }
};

const postBestEffort = async (path: string, body?: unknown, timeoutMs = 5000): Promise<void> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    await request<void>(path, {
      method: 'POST',
      signal: controller.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // Telemetry must never affect the checkout path.
  } finally {
    window.clearTimeout(timeout);
  }
};

const toQuery = (params: Record<string, unknown> = {}): string => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') {
      return;
    }
    sp.set(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

const billingClient = Object.freeze({
  get: async <T>(path: string, params?: Record<string, unknown>): Promise<T> => withTimeout(
    (signal) => request<T>(`${path}${toQuery(params)}`, {
      method: 'GET',
      signal
    })
  ),
  post: async <T>(path: string, body?: unknown): Promise<T> => withTimeout(
    (signal) => request<T>(path, {
      method: 'POST',
      signal,
      body: body === undefined ? undefined : JSON.stringify(body)
    })
  ),
  patch: async <T>(path: string, body?: unknown): Promise<T> => withTimeout(
    (signal) => request<T>(path, {
      method: 'PATCH',
      signal,
      body: body === undefined ? undefined : JSON.stringify(body)
    })
  ),
  delete: async <T>(path: string, body?: unknown): Promise<T> => withTimeout(
    (signal) => request<T>(path, {
      method: 'DELETE',
      signal,
      body: body === undefined ? undefined : JSON.stringify(body)
    })
  ),
  postBestEffort
});

export default billingClient;
